import axios from 'axios';
import { API_BASE_URL } from '../config';

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // This is important for handling cookies in cross-origin requests
});

// Add a request interceptor to add auth token if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('API Error:', error.response.data);
      return Promise.reject(error.response.data);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
      return Promise.reject(new Error('No response received from server'));
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Request setup error:', error.message);
      return Promise.reject(error);
    }
  }
);

export interface Challenge {
  id: number;
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  points: number;
  validation_script: string;
  docker_image: string;
  setup_script?: string;
  max_attempts: number;
  time_limit_minutes: number;
  is_active: boolean;
  tags: string[];
  prerequisites: number[];
  hints: string[];
}

export interface ChallengeListParams {
  skip?: number;
  limit?: number;
  search?: string;
  difficulty?: 'Easy' | 'Medium' | 'Hard';
  is_active?: boolean;
}

export interface CreateChallengeData {
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  points: number;
  validation_script: string;
  docker_image?: string;
  setup_script?: string;
  max_attempts?: number;
  time_limit_minutes?: number;
  is_active?: boolean;
  tags?: string[];
  prerequisites?: number[];
  hints?: string[];
}

export interface UpdateChallengeData extends Partial<CreateChallengeData> {}

export interface ChallengeResponse {
  data: Challenge[];
  total: number;
  page: number;
  per_page: number;
}

export const challengeService = {
  async listChallenges(params: ChallengeListParams = {}): Promise<ChallengeResponse> {
    const response = await api.get('/v1/admin/challenges', { params });
    return response.data;
  },

  async createChallenge(data: CreateChallengeData): Promise<Challenge> {
    const response = await api.post('/v1/admin/challenges', data);
    return response.data;
  },

  async updateChallenge(challengeId: number, data: UpdateChallengeData): Promise<Challenge> {
    const response = await api.patch(`/v1/admin/challenges/${challengeId}`, data);
    return response.data;
  },

  async deleteChallenge(challengeId: number): Promise<void> {
    await api.delete(`/v1/admin/challenges/${challengeId}`);
  },

  async toggleChallengeStatus(challengeId: number): Promise<Challenge> {
    const response = await api.patch(`/v1/admin/challenges/${challengeId}/toggle`);
    return response.data;
  }
}; 